// Doc accumulation: the single source of truth for the live template doc.
// Section text lives in session state (persisted), so resuming a session
// renders the doc instantly instead of re-routing every decision through the
// model. The UI layers diff/ageing rendering on top; the headless harness
// uses this module directly.

import { complete } from "../api/model";
import { PRI } from "../api/scheduler";
import { parseModelJSON, salvageSectionUpdates } from "../lib/json-repair";
import { tidyProse } from "../lib/text";
import { DOC_SECTIONS, SECTION_GUIDE, normalizeUpdates, routeDecisionIntoDoc } from "./doc";
import { save } from "./persistence";
import { docCompletePrompt } from "./prompts";
import { groundingLine, state, type DecisionNode } from "./state";
import { traceParseOutcome } from "./telemetry";
import { traceText } from "./synthesis";

export interface AppliedUpdate { section: string; prev: string; next: string }

export function docSectionText(section: string): string {
  return state.doc.secs[section] || "";
}

export function docFilledCount(): number {
  return DOC_SECTIONS.filter((s) => state.doc.secs[s]).length;
}

export function isDocFolded(nodeId: string): boolean {
  return state.doc.folded.includes(nodeId);
}

function applySection(section: string, text: string): AppliedUpdate | null {
  const prev = state.doc.secs[section] || "";
  if (!text || text === prev) return null;
  state.doc.secs[section] = text;
  return { section, prev, next: text };
}

/** Rejected options land in "Alternative Solutions Considered" — free, no model. */
function applyAlternatives(node: DecisionNode): AppliedUpdate | null {
  const rejected = node.options.filter((_, i) => i !== node.chosenIndex);
  if (!rejected.length || node.chosenIndex == null) return null;
  const sec = "Alternative Solutions Considered";
  const prev = state.doc.secs[sec] || "";
  const line = `- ${node.question} rejected: ${rejected.map((o) => o.label).join("; ")} — chose ${node.options[node.chosenIndex]!.label}.`;
  return applySection(sec, prev ? `${prev}\n${line}` : line);
}

/**
 * Route one resolved decision into the doc and persist. Returns the applied
 * updates (with previous text, so the UI can word-diff them).
 */
export async function foldDecisionIntoDoc(node: DecisionNode): Promise<AppliedUpdate[]> {
  if (node.chosenIndex == null || isDocFolded(node.id)) return [];
  state.doc.folded.push(node.id);
  const current = Object.entries(state.doc.secs)
    .filter(([, text]) => text)
    .map(([section, text]) => ({ section, text: text.slice(0, 300) }));
  const updates = await routeDecisionIntoDoc(node, current);
  const applied: AppliedUpdate[] = [];
  const alt = applyAlternatives(node);
  if (alt) applied.push(alt);
  for (const u of updates) {
    const a = applySection(u.section, u.content);
    if (a) applied.push(a);
  }
  save();
  return applied;
}

/**
 * Completion sweep: fill sections that are still empty from the full trace,
 * a few per call. The biggest lever for "the artefact is a filled template".
 */
export async function completeEmptyDocSections(
  onUpdate?: (u: AppliedUpdate) => void,
  batchSize = 5,
): Promise<AppliedUpdate[]> {
  const empty = DOC_SECTIONS.filter((s) => !state.doc.secs[s]);
  const trace = traceText();
  if (!empty.length || !trace.trim()) return [];
  const applied: AppliedUpdate[] = [];
  const byName = new Map(DOC_SECTIONS.map((s) => [s.toLowerCase(), s]));
  for (let i = 0; i < empty.length; i += batchSize) {
    const batch = empty.slice(i, i + batchSize);
    const filledSummary = Object.entries(state.doc.secs)
      .filter(([, t]) => t)
      .map(([s, t]) => `### ${s}\n${t.slice(0, 160)}`).join("\n");
    const prompt = docCompletePrompt(
      { idea: state.idea, grounding: groundingLine("n/a"), facts: state.facts, constraints: state.constraints },
      trace, filledSummary, batch, SECTION_GUIDE,
    );
    try {
      const raw = await complete(prompt, { max: 900, temp: 0.4, priority: PRI.GROUND, kind: "doc.complete" });
      let { data, parse } = parseModelJSON(raw);
      if ((!data || !Array.isArray(data.updates)) && raw.includes('"section"')) {
        const pairs = salvageSectionUpdates(raw);
        if (pairs.length) { data = { updates: pairs }; parse = "repaired"; }
      }
      traceParseOutcome("doc.complete", parse, raw);
      for (const u of normalizeUpdates(data?.updates || []).slice(0, batchSize + 1)) {
        const section = byName.get(String(u.section || "").trim().toLowerCase());
        const content = String(u.content || "").split("\n").map((l: string) => tidyProse(l)).filter(Boolean).join("\n");
        if (!section || !content || content === "N/A") continue;
        if (!batch.includes(section)) continue;   // don't let it rewrite filled sections
        const a = applySection(section, content);
        if (a) { applied.push(a); onUpdate?.(a); }
      }
    } catch { /* sweep is best-effort; move to the next batch */ }
  }
  save();
  return applied;
}

/** Assemble the accumulated template as markdown — instant, no model call. */
export function templateMarkdown(): string {
  const head = [
    `# ${state.idea || "Design Doc"}`,
    "",
    `**Author**: Lattice session \`${state.sessionId}\``,
    `**Date**: ${new Date().toISOString().slice(0, 10)}`,
    "**Stakeholders** (sign-off required):",
    "**Status**: Draft — assembled live from the decision trace",
    "",
  ];
  const body = DOC_SECTIONS.flatMap((s) => [`## ${s}`, "", state.doc.secs[s] || "N/A", ""]);
  return head.concat(body).join("\n");
}
